import {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { match } from 'ts-pattern';
import { useLoaderData } from '~/app/features/crew/claim-line-items/resolution/resolution';
import api from '~/utils/api';
import { AsyncReturnType } from '~/utils/types';

export type Product = AsyncReturnType<
  typeof api.fetchProductVariantInfo
>['products'][number];

export type Variant = Product['variants'][number];
export type Option = Variant['options'][number];

export function useExchangeProducts() {
  const { exchangeProducts, draftClaimLineItem } = useLoaderData();

  return useMemo(() => {
    const claimLineItemIdFromPlatform =
      draftClaimLineItem.lineItem.productIdFromPlatform;

    return [...exchangeProducts].sort((a, b) => {
      // put the claim product first, if it exists in the exchange products
      if (a.idFromPlatform === claimLineItemIdFromPlatform) return -1;
      if (b.idFromPlatform === claimLineItemIdFromPlatform) return 1;
      return 0;
    });
  }, [draftClaimLineItem.lineItem.productIdFromPlatform, exchangeProducts]);
}

export type ExchangeFilters = ReturnType<typeof useExchangeFilters>;
export function useExchangeFilters() {
  const { exchangeFilters } = useLoaderData();
  return useMemo(
    () =>
      ({
        option: new Map(
          exchangeFilters.map((filter) => [
            filter.name,
            new Set(filter.values),
          ]),
        ),
      }) satisfies Record<
        (typeof exchangeFilters)[number]['type'], // * if/when this has additional types, the actual filtering will need to account for how to filter by that type
        Map<string, Set<string>>
      >,
    [exchangeFilters],
  );
}

type State = {
  selectedOptions: Record<string, string>;
  selectedProduct: Product | null;
  selectedVariant: Variant | null;
};

type Action =
  | { type: 'setProduct'; product: Product | null }
  | { type: 'toggleOption'; option: Option }
  | { type: 'setVariant'; product: Product; variant: Variant };

/**
 * Denotes that the `product` has some variant which includes all of the `options`.
 * The resulting `variant`s will be those that match.
 * If only one variant is returned, that means all `options` have a selection.
 */
export function variantsForOptions(
  product: Product,
  options: Record<string, string>,
) {
  const optionEntries = Object.entries(options);
  return product.variants.filter((variant) =>
    optionEntries.every(([name, value]) =>
      variant.options.some((o) => o.name === name && o.value === value),
    ),
  );
}

const ExchangeContext = createContext<
  [state: State, dispatch: Dispatch<Action>] | null
>(null);

function stateFromProduct(product: Product | null) {
  if (!product) {
    return {
      selectedProduct: null,
      selectedVariant: null,
      selectedOptions: {},
    } satisfies State;
  }

  const defaultVariant =
    product.hasOnlyDefaultVariant || product.variants.length === 1
      ? product.variants[0]
      : null;

  return {
    selectedProduct: product,
    selectedVariant: defaultVariant ?? null,
    selectedOptions: {},
    // ? verify this?
  } satisfies State;
}

function exchangeReducer(state: State, action: Action) {
  return match(action)
    .returnType<State>()
    .with({ type: 'setProduct' }, ({ product }) => ({
      ...state,
      ...stateFromProduct(product),
    }))
    .with({ type: 'setVariant' }, ({ product, variant }) => {
      const selectedOptions = variant.options.reduce<Record<string, string>>(
        (acc, option) => ({ ...acc, [option.name]: option.value }),
        {},
      );

      return {
        ...state,
        selectedVariant: variant,
        selectedProduct: product,
        selectedOptions,
      };
    })
    .with({ type: 'toggleOption' }, ({ option }) => {
      // update selected options
      let selectedOptions: Record<string, string> = {};
      if (state.selectedOptions[option.name] === option.value) {
        const { [option.name]: removed, ...rest } = state.selectedOptions;
        selectedOptions = rest;
      } else {
        selectedOptions = {
          ...state.selectedOptions,
          [option.name]: option.value,
        };
      }

      // decide if a specific variant is selected
      let selectedVariant: Variant | null = null;
      const { selectedProduct } = state;

      if (selectedProduct) {
        const possibleVariants = variantsForOptions(
          selectedProduct,
          selectedOptions,
        );
        const eachOptionHasSelection = selectedProduct.options.every(
          (o) => o.name in selectedOptions,
        );
        if (possibleVariants.length === 1 && eachOptionHasSelection) {
          selectedVariant = possibleVariants[0] ?? null;
        }
      }

      return {
        ...state,
        selectedOptions,
        selectedVariant,
      };
    })
    .exhaustive();
}

export function useExchangeSelection() {
  const context = useContext(ExchangeContext);
  if (!context) {
    throw new Error(
      'useExchangeSelection must be used within an ExchangeOptionsProvider',
    );
  }
  return context;
}

export function ExchangeOptionsProvider({ children }: PropsWithChildren) {
  const exchangeProducts = useExchangeProducts();
  const { aiExchangeRecommendation } = useLoaderData();

  const recommendedSelection = (() => {
    if (!aiExchangeRecommendation) return null;
    const product = exchangeProducts.find(
      (p) =>
        p.idFromPlatform === aiExchangeRecommendation.product.idFromPlatform,
    );
    const variant = product?.variants.find(
      (v) =>
        v.idFromPlatform === aiExchangeRecommendation.variant.idFromPlatform,
    );
    if (!product || !variant) return null;
    return { product, variant };
  })();

  const reducer = useReducer(
    exchangeReducer,
    recommendedSelection
      ? {
          selectedProduct: recommendedSelection.product,
          selectedVariant: recommendedSelection.variant,
          selectedOptions: recommendedSelection.variant.options.reduce<
            Record<string, string>
          >((acc, option) => ({ ...acc, [option.name]: option.value }), {}),
        }
      : // default to the first product if there is only one; i.e. product exchange groups are disabled
        stateFromProduct(
          exchangeProducts.length === 1 && exchangeProducts[0]
            ? exchangeProducts[0]
            : null,
        ),
  );

  return (
    <ExchangeContext.Provider value={reducer}>
      {children}
    </ExchangeContext.Provider>
  );
}
